import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import _ from "lodash";
import { Moment } from "moment";

import { SearchableSelect, Button, DateRangePicker } from "..";
import { LabMachineAnalysisFiletersFormProps } from "../../utils/types/components";
import {
  formSchema,
  getFormInitValues,
} from "../../utils/functions/view/labMachineAnalysisFiletersFormFns";

export const LabMachineAnalysisFiletersForm = ({
  registers,
  onSubmit,
}: LabMachineAnalysisFiletersFormProps): React.ReactElement => {
  const [optsFilt, setOptsFilt] = useState<Record<string, string[]>>({
    states: [],
    machines: [],
  });

  const {
    values,
    setFieldValue,
    errors,
    handleSubmit,
    isValid,
    resetForm,
  } = useFormik({
    initialValues: getFormInitValues({
      initStates: optsFilt.states,
      initMachines: optsFilt.machines,
    }),
    onSubmit,
    validationSchema: formSchema,
  });

  useEffect(() => {
    const { states, machines } = registers.reduce(
      (acc, { estado, equipo_componente }) => ({
        states: acc.states.includes(estado)
          ? acc.states
          : [...acc.states, estado],
        machines: acc.machines.includes(equipo_componente)
          ? acc.machines
          : [...acc.machines, equipo_componente],
      }),
      { states: [], machines: [] },
    );

    resetForm();
    setOptsFilt({ ...optsFilt, machines, states });
    setFieldValue("machines", machines);
    setFieldValue("states", states);
  }, [registers]);

  const getFilter = (id, newValue) => (item) => {
    const filterByDates = ({ fecha_muestra }, dates = values.sampleDates) => {
      if (dates[0] && new Date(dates[0].toString()) > new Date(fecha_muestra))
        return false;

      if (dates[1] && new Date(dates[1].toString()) < new Date(fecha_muestra))
        return false;

      return true;
    };

    const filterByState = ({ estado }, states = values.states) => {
      return states.includes(estado);
    };

    const filterByMachine = (
      { equipo_componente },
      machines = values.machines,
    ) => {
      return machines.includes(equipo_componente);
    };

    if (id === "sampleDates") {
      return filterByDates(item, newValue);
    }

    if (id === "states") {
      if (filterByDates(item)) return filterByState(item, newValue);

      return false;
    }

    if (id === "machines") {
      if (filterByDates(item))
        if (filterByState(item)) return filterByMachine(item);

      return false;
    }

    return false;
  };

  const reduceRegister = (acc, { estado, equipo_componente }) => ({
    states: acc.states.includes(estado) ? acc.states : [...acc.states, estado],
    machines: acc.machines.includes(equipo_componente)
      ? acc.machines
      : [...acc.machines, equipo_componente],
  });

  const onChange = async (id: string, v: string[] | [Moment, Moment]) => {
    const filter = getFilter(id, v);

    const { states, machines } = registers
      .filter(filter)
      .reduce(reduceRegister, { states: [], machines: [] });

    if (id === "sampleDates") {
      setOptsFilt({ ...optsFilt, states, machines });
      setFieldValue("states", states);
      setFieldValue("machines", machines);
    }

    if (id === "states") {
      setOptsFilt({ ...optsFilt, machines });
      setFieldValue("states", states);
      setFieldValue("machines", machines);
    }

    setFieldValue(id, v);
  };

  const selectAll = () => {
    onChange("sampleDates", [null, null]);
  };

  return (
    <>
      <DateRangePicker
        startDateLabel="Desde fecha de muestra"
        endDateLabel="Hasta fecha de muestra"
        startValue={values.sampleDates[0]}
        endValue={values.sampleDates[1]}
        onChange={(v) => onChange("sampleDates", v)}
        error={Boolean(errors.sampleDates)}
      />
      <SearchableSelect
        label="Estados"
        value={values.states}
        options={optsFilt.states}
        getOptionLabel={(v: string) => v}
        onChange={(v) => onChange("states", v as string[])}
        style={{ marginTop: 20 }}
        error={Boolean(errors.states)}
        helperText={_.toString(errors.states)}
        multiple
      />
      <SearchableSelect
        label="Equipos"
        value={values.machines}
        options={optsFilt.machines}
        getOptionLabel={(v: string) => v}
        onChange={(v) => onChange("machines", v as string[])}
        style={{
          marginTop: 20,
          maxHeight: 200,
          overflowX: "auto",
          paddingTop: 5,
          paddingBottom: 5,
        }}
        error={Boolean(errors.machines)}
        helperText={_.toString(errors.machines)}
        multiple
      />

      <Button
        style={{ marginTop: "20px" }}
        onClick={handleSubmit}
        disabled={!isValid || _.isEmpty(registers)}>
        Filtrar
      </Button>

      <Button
        style={{ marginTop: "20px", marginLeft: "10px" }}
        onClick={selectAll}>
        Seleccionar todo
      </Button>
    </>
  );
};
